/**
 * API Path Definitions for ADDAPT
 * This file defines API paths and structures.
 * Actual endpoint URLs come from settings.json which is loaded at runtime.
 * 
 * Why this separation?
 * - settings.json: Environment-specific URLs (dev/beta/gamma/prod)
 * - This file: API path structure that remains constant across environments
 */

export const API_PATHS = {
  // Profile Management
  PROFILES: {
    LIST: '/ListProfiles',              // ✅ Implemented
    GET: '/GetProfile',                 // ✅ Implemented
    CREATE: '/CreateProfile',           // ✅ Implemented
    LDAP_GROUPS: '/GetUserLdapGroups',  // ✅ Implemented - Get user's LDAP groups
  },
  
  // Job Management
  JOBS: {
    LIST: '/ListJobs',                  // ✅ Implemented - List jobs for a profile
    LIST_RUNS: '/ListJobRuns',          // ✅ Implemented - List runs for a job
    GET: '/GetJob',                     // ✅ Implemented - Get job details
    GET_DRAFT: '/GetDraftJob',          // ✅ Implemented - Create draft job with 1-hour TTL
    CREATE: '/CreateJob',               // ✅ Implemented - Convert draft to release job
    EDIT: '/EditJob',                   // ✅ Implemented - Update job details
    RUN: '/RunJob',                     // ✅ Implemented - Run a job
    RUN_STATUS: '/GetJobRunStatus',     // ✅ Implemented - Get job run status
  },
  
  // Document Processing (S3)
  DOCUMENTS: {
    PRESIGNED_UPLOAD: '/GetPresignedUploadUrl',     // ✅ Implemented - Upload files to S3
    PRESIGNED_DOWNLOAD: '/GetPresignedDownloadUrl', // ✅ Implemented - Download files from S3
  },
  
  // Schedule Management - Aligned with backend Smithy operations
  SCHEDULES: {
    LIST: '/ListSchedules',             // List schedules for a job
    GET: '/GetSchedule',                // Get schedule details
    CREATE: '/CreateSchedule',          // Create a new schedule
    UPDATE: '/UpdateSchedule',          // Update schedule details
    DELETE: '/DeleteSchedule',          // Delete a schedule
    UPDATE_STATUS: '/UpdateScheduleStatus', // Enable/disable a schedule (renamed from TOGGLE)
    EXECUTIONS: '/ListScheduleExecutions', // List schedule execution history
  },
  
  // Code Generation
  CODE_GENERATION: {
    REGENERATE: '/RegenerateCode',      // ✅ Implemented - Regenerate code with feedback
  },

  // Review & Approval
  REVIEW: {
    SUBMIT: '/SubmitForReview',         // Submit job for CRUX CR review
  },

  // SOX Compliance & Andes Publishing
  COMPLIANCE: {
    SUBMIT: '/SubmitComplianceQuestionnaire',  // Submit SOX compliance questionnaire
    PUBLISH_ANDES: '/PublishToAndes',           // Publish dataset to Andes
  },

  // Test endpoint
  TEST: {
    GET_ALL_BEERS: '/GetAllBeers',      // ✅ Used in App.tsx
  }
};

/**
 * Helper function to build full API URL
 * @param path - The API path from API_PATHS
 * @param params - Parameters to replace in the path
 * @returns Full URL with parameters replaced
 */
export function buildApiUrl(path: string, params?: Record<string, string>): string {
  let url = path;
  
  // Replace path parameters
  if (params) {
    Object.keys(params).forEach(key => {
      url = url.replace(`:${key}`, params[key]);
    });
  }
  
  return url;
}

/**
 * Get WebSocket URL from settings
 * @param type - 'default' or 'ai'
 * @returns WebSocket URL or null if not configured
 */
export function getWebSocketUrl(type: 'default' | 'ai' = 'default'): string | null {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.webSocketConfig?.[type] || null;
}

/**
 * Get AI HTTP endpoint from settings
 * @returns AI HTTP endpoint URL or null if not configured
 */
export function getAIHttpEndpoint(): string | null {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.aiHttpEndpoint || null;
}

/**
 * Check if a feature is enabled
 * @param feature - Feature name
 * @returns boolean indicating if feature is enabled
 */
export function isFeatureEnabled(feature: string): boolean {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.features?.[feature] === true;
}